<template>
  <div class="thank-you-step">
    <div class="thank-you-content">
      <!-- Text Content -->
      <div class="text-section">
        <h2 class="step-title">{{ step.title }}</h2>
        <p v-if="step.subtitle" class="step-subtitle">{{ step.subtitle }}</p>
      </div>

      <!-- Avatar -->
      <div class="avatar-container">
        <img :src="avatarImage" alt="AI Assistant" class="ai-avatar" />
      </div>
    </div>

    <!-- Action Button -->
    <div class="action-section">
      <custom-button
        @click="viewResults"
        class="results-button"
        :loading="surveyStore.surveyModal.isLoading"
        rounded
      >
        Посмотреть результаты
      </custom-button>
    </div>

    <!-- Loading Indicator -->
    <div v-if="surveyStore.surveyModal.isLoading" class="loading-section">
      <div class="loading-spinner"></div>
      <p>Анализируем ваши параметры...</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useRouter } from 'vue-router'
import CustomButton from '@/components/UI/CustomButtom/CustomButton.vue'
import { useSurveyModal } from '@/features/survey/store'
import type { SurveyStep } from '@/features/survey/types/survey-config'

interface Props {
  step: SurveyStep & { type: 'thank_you' }
}

const props = defineProps<Props>()
const surveyStore = useSurveyModal()
const router = useRouter()

const avatarImage = '/img/survey-avatar.png'

async function viewResults() {
  try {
    // Отправляем данные на бекенд
    await surveyStore.submitSurvey()
    
    // Закрываем модальное окно
    surveyStore.hideSurveyModal()
    
    // Редиректим на страницу результатов
    router.push('/profile/sizes')
  } catch (error) {
    console.error('Error submitting survey:', error)
    // В случае ошибки все равно закрываем модалку и редиректим
    surveyStore.hideSurveyModal()
    router.push('/profile/sizes')
  }
}
</script>

<style scoped>
.thank-you-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px 20px;
  text-align: left;
  height: 100%;
}

.thank-you-content {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  flex: 1;
}

.avatar-container {
  display: flex;
  justify-content: center;
  width: 100%;
}

.ai-avatar {
  height: 353px;
  border-radius: 20px;
  object-fit: cover;
}

.text-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
}

.step-title {
  font-size: 32px;
  font-weight: 700;
  font-family: 'Gilroy', sans-serif;
  color: #232f45;
  margin: 0;
  line-height: 1.09;
  letter-spacing: -0.02em;
}

.step-subtitle {
  font-size: 16px;
  font-weight: 400;
  font-family: 'Inter', sans-serif;
  color: rgba(35, 47, 69, 0.8);
  line-height: 1.3;
  margin: 0;
  letter-spacing: -0.04em;
}

.action-section {
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  margin-top: 20px;
}

.results-button {
  display: flex;
  width: 100%;
  cursor: pointer;
  transition: all 0.3s ease;
}

.loading-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  color: #666;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #007aff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-section p {
  font-size: 14px;
  margin: 0;
  font-weight: 500;
}

@media (min-width: 1919px) {
  .thank-you-content {
    padding: 40px 120px;
  }
}

@media (min-width: 1025px) {
  .thank-you-step {
    padding: 20px;
    text-align: left;
  }

  .text-section {
    max-height: 234px;
    overflow: auto;
  }

  .results-button {
    width:237px !important;
  }

  .thank-you-content {
    flex-direction: row-reverse;
    align-items: center;
    gap: 40px;
    max-width: none;
    width: 100%;
    padding: 40px
  }

  .avatar-container {
    margin-bottom: 0;
    flex-shrink: 0;
    width: auto;
  }

  .ai-avatar {
    width: 234px;
    height: 234px;
    border-radius: 20px;
    object-fit: cover;
  }

  .text-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-bottom: 0;
    flex: 1;
  }

  .step-title {
    font-size: 48px;
    font-weight: 700;
    font-family: 'Gilroy', sans-serif;
    color: #232f45;
    margin: 0;
    line-height: 1.2;
    letter-spacing: -0.04em;
  }

  .step-subtitle {
    font-size: 20px;
    font-weight: 400;
    font-family: 'Inter', sans-serif;
    color: rgba(35, 47, 69, 0.8);
    line-height: 1.3;
    margin: 0;
    letter-spacing: -0.04em;
  }

  .action-section {
    display: flex;
    justify-content: flex-end;
    width: 100%;
    padding: 20px;
    margin-top: 0;
    align-items: flex-end;
  }

  .results-button {
    width: auto;
  }
}
</style>

